import {
  useElement,
  useState,
  useStaleLayout,
  useRect,
  useEffect,
} from '@nebula.js/stardust';
import picassojs from 'picasso.js';
import picassoHammer from 'picasso-plugin-hammer';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
// import topo from 'assets/ne_110m_admin_0_countries_lakes.json';
import topo from 'assets/countries-topojson.json';
import { easeExpOut as ease } from 'd3-ease';
import { timer } from 'd3-timer';
import { interpolate } from 'd3-interpolate';  //eslint-disable-line

let transition = null;
const stopTransition = () => {
  if (transition) {
    transition.stop();
    transition = null;
  }
};

const distance = (p1, p2) => {  //eslint-disable-line
  const a = p1.x - p2.x;
  const b = p1.y - p2.y;
  const d = Math.sqrt(a ** 2 + b ** 2);
  return d || 0;
};

// from https://observablehq.com/@d3/clustered-bubbles
/* eslint-disable */
function centroid(nodes) {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const d of nodes) {
    let k = d.r ** 2;
    x += d.x * k;
    y += d.y * k;
    z += k;
  }
  return {x: x / z, y: y / z};
}

function forceCluster() {
  const strength = 0.2;
  let nodes;

  function force(alpha) {
    const centroids = d3.rollup(nodes, centroid, d => d.data.group);
    const l = alpha * strength;
    for (const d of nodes) {
      const {x: cx, y: cy} = centroids.get(d.data.group);
      d.vx -= (d.x - cx) * l;
      d.vy -= (d.y - cy) * l;
    }
  }

  force.initialize = _ => nodes = _;

  return force;
}

function forceCollide() {
  const alpha = 1.0; // fixed for greater rigidity!
  const padding1 = 5; // separation between same-color nodes
  const padding2 = 12; // separation between different-color nodes
  let nodes;
  let maxRadius;

  function force() {
    const quadtree = d3.quadtree(nodes, d => d.x, d => d.y);
    for (const d of nodes) {
      const r = d.r + maxRadius;
      const nx1 = d.x - r, ny1 = d.y - r;
      const nx2 = d.x + r, ny2 = d.y + r;
      quadtree.visit((q, x1, y1, x2, y2) => {
        if (!q.length) do {
          if (q.data !== d) {
            const r = d.r + q.data.r + (d.data.group === q.data.data.group ? padding1 : padding2);
            let x = d.x - q.data.x, y = d.y - q.data.y, l = Math.hypot(x, y);
            if (l < r) {
              l = (l - r) / l * alpha;
              d.x -= x *= l, d.y -= y *= l;
              q.data.x += x, q.data.y += y;
            }
          }
        } while (q = q.next);
        return x1 > nx2 || x2 < nx1 || y1 > ny2 || y2 < ny1;
      });
    }
  }

  force.initialize = _ => maxRadius = d3.max(nodes = _, d => d.r) + Math.max(padding1, padding2);

  return force;
}
/* eslint-enable */

export default function supernova() {
  const picasso = picassojs();
  picasso.use(picassoHammer);

  return {
    qae: {
      properties: {
        qHyperCubeDef: {
          qDimensions: [],
          qMeasures: [],
          qInitialDataFetch: [{ qWidth: 2, qHeight: 5000 }],
          qSuppressZero: true,
          qSuppressMissing: true,
        },
        showTitles: true,
        title: '',
        subtitle: '',
        footnote: '',
      },
      data: {
        targets: [
          {
            path: '/qHyperCubeDef',
            dimensions: {
              min: 1,
              max: 1,
            },
            measures: {
              min: 1,
              max: 1,
            },
          },
        ],
      },
    },
    component() {
      const element = useElement();
      const layout = useStaleLayout();
      const rect = useRect();

      const [instance, setInstance] = useState();
      const [data, setData] = useState([]);
      const [prevData, setPrevData] = useState([]);

      useEffect(() => {
        // const p = picasso().chart({
        const p = picasso({ renderer: { prio: ['canvas'] } }).chart({
          element,
          data: [],
          settings: {},
        });

        setInstance(p);

        return () => {
          p.destroy();
        };
      }, []);

      useEffect(() => {
        // set scales
        const maxMedals = layout.qHyperCube.qDataPages[0].qMatrix
          .map((arr) => arr[1])
          .reduce((prev, curr) => (curr.qNum > prev ? curr.qNum : prev), 0);
        const radiusScale = d3.scaleSqrt().domain([0, maxMedals]).range([0, 20]);
        // calculate geometries from topojson and qlik data
        const geometries = layout.qHyperCube.qDataPages[0].qMatrix.map((row) => {
          const g = JSON.parse(JSON.stringify(topo.objects.countries.geometries.find((geometry) => geometry.properties.ISO_A3 === row[0].qAttrExps.qValues[0].qText || geometry.properties.ADMIN === row[0].qText)));
          if (!g) return undefined;
          g.properties.country = row[0].qText;
          g.properties.iso = row[0].qAttrExps.qValues[0].qNum;
          g.properties.continent = row[0].qAttrExps.qValues[1].qText;
          g.properties.yearCity = row[0].qAttrExps.qValues[2].qText;
          g.properties.medals = row[1].qNum;
          g.properties.medalsG = row[1].qAttrExps.qValues[0].qNum;
          g.properties.medalsS = row[1].qAttrExps.qValues[1].qNum;
          g.properties.medalsB = row[1].qAttrExps.qValues[2].qNum;
          return g;
        }).filter((geometry) => geometry !== undefined);
        const currTopo = {
          ...topo,
          objects: {
            ...topo.objects,
            countries: {
              ...topo.objects.countries,
              geometries,
            },
          },
        };

        // create nodes and neighbors
        /* eslint-disable */
        const nodes = topojson.feature(currTopo, currTopo.objects.countries).features;
        nodes.forEach(function(node, i) {
          const centroid = d3.geoPath().centroid(node);
          node.idx = i;
          node.x = centroid[0];
          node.y = centroid[1];
          node.r = radiusScale(node.properties.medals);
          node.data = {
            group: node.properties.continent
          };
        });

        // regular neighbors
        // const neighbors = topojson.neighbors(currTopo.objects.countries.geometries);
        // const links = d3.merge(neighbors.map(function(neighborSet, i) {
        //   return neighborSet.filter(j => nodes[j]).map(function(j) {
        //     return {source: i, target: j, distance: nodes[i].r + nodes[j].r + 1};
        //   });
        // }));

        // continental neighbors
        // let links = [];
        // const continents = [...new Set(currTopo.objects.countries.geometries.map((geometry) => geometry.properties.continent))];
        // continents.forEach(function(continent, i) {
        //   const continentNodes = nodes.filter((node) => node.properties.continent === continent);
        //   const continentGeometries = currTopo.objects.countries.geometries.filter((geometry) => geometry.properties.continent === continent);
        //   const neighbors = topojson.neighbors(continentGeometries);
        //   links = links.concat(d3.merge(neighbors.map(function(neighborSet, i) {
        //     return neighborSet.filter(j => continentNodes[j]).map(function(j) {
        //       return {source: continentNodes[i].idx, target: continentNodes[j].idx, distance: continentNodes[i].r + continentNodes[j].r + 3};
        //     });
        //   })));
        // });

        // all continent nodes as neighbors
        // let links = [];
        // const continents = [...new Set(currTopo.objects.countries.geometries.map((geometry) => geometry.properties.continent))];
        // continents.forEach(function(continent) {
        //   const continentNodes = nodes.filter((node) => node.properties.continent === continent);
        //   for (let i = 0; i < continentNodes.length; i++) {
        //     for (let j = i; j < continentNodes.length; j++) {
        //       links.push({
        //         source: continentNodes[i].idx, 
        //         target: continentNodes[j].idx, 
        //         distance: continentNodes[i].r + continentNodes[j].r + 3
        //       });
        //     }
        //   }
        // });

        // all nodes linked, nodes not on same continent pushed away, nodes on same continent pulled closer
        let links = [];
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i; j < nodes.length; j++) {
            links.push({
              source: nodes[i].idx, 
              target: nodes[j].idx, 
              distance: nodes[i].properties.continent === nodes[j].properties.continent 
                ? nodes[i].r + nodes[j].r
                : Math.max(80, distance(nodes[i], nodes[j]))
            });
          }
        }
        /* eslint-enable */

        // const width = 360;
        // const height = 180;
        const simulation = d3.forceSimulation(nodes)
          // .force('cx', d3.forceX().x(() => width / 2).strength(0.02))
          // .force('cy', d3.forceY().y(() => height / 2).strength(0.02))
          // .force('charge', d3.forceManyBody())
          .force('x', d3.forceX().x((d) => d.x).strength(4))
          .force('y', d3.forceY().y((d) => d.y).strength(4))
          .force('center', d3.forceCenter(0, 0))
          .force('link', d3.forceLink(links).distance((d) => d.distance).strength(0.5))
          .force('collide', d3.forceCollide().radius((d) => d.r + 3).strength(0.5).iterations(5))
          // .force('cluster', forceCluster())
          // .force('collide', forceCollide())
          .stop();

        while (simulation.alpha() > 0.01) {
          simulation.tick();
        }

        // Format data to pass to picasso
        const _data = [
          ['country', 'continent', 'yearCity', 'medals', 'medalsG', 'medalsS', 'medalsB', 'x', 'y', 'r'],
          ...nodes.map((node) => [
            node.properties.country,
            node.properties.continent,
            node.properties.yearCity,
            node.properties.medals,
            node.properties.medalsG,
            node.properties.medalsS,
            node.properties.medalsB,
            node.x,
            node.y,
            node.r,
          ]),
        ];

        setData(_data);
      }, [layout]);

      useEffect(() => {
        if (!instance || !data.length) {
          return;
        }
        const colorScale = d3.scaleOrdinal()
          .domain(['North America', 'South America', 'Europe', 'Asia', 'Africa', 'Oceania'])
          .range(['#8A85C6', '#87205D', '#10CFC9', '#AC4D58', '#E0BD8D', '#006580']);

        if (transition) {
          stopTransition();
        }

        const duration = 400;
        // get selected countries
        const selectedCountries = layout.qHyperCube.qDataPages[0].qMatrix.filter((row) => row[0].qState === 'S').map((row) => row[0].qText);

        transition = timer((elapsed) => {
          const t = Math.min(1, ease(elapsed / duration));
          // const tweenData = interpolate(prevData, data)(t);
          instance.update({
            data: [
              {
                type: 'matrix',
                data,
              },
            ],
            settings: {
              strategy: {
                logicalSize: {
                  width: 360,
                  height: 180,
                  preserveAspectRatio: true,
                },
              },
              scales: {
                y: {
                  min: -90,
                  max: 90,
                  invert: true,
                },
                x: {
                  min: -180,
                  max: 180,
                },
                r: {
                  data: { field: 'r' },
                  include: [0, 20],
                },
              },
              components: [
                {
                  key: 'p',
                  type: 'point',
                  data: {
                    extract: {
                      field: 'country',
                      props: {
                        x: { field: 'x' },
                        y: { field: 'y' },
                        size: { field: 'r' },
                        color: { field: 'continent' },
                        country: { field: 'country' },
                        yearCity: { field: 'yearCity' },
                        medals: { field: 'medals' },
                        medalsG: { field: 'medalsG' },
                        medalsS: { field: 'medalsS' },
                        medalsB: { field: 'medalsB' },
                      },
                    },
                  },
                  settings: {
                    x: {
                      scale: 'x',
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0] === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.scale(prev[7]),
                            d.scale(d.datum.x.value),
                          )(t);
                        }
                        return d.scale(d.datum.x.value);
                      },
                    },
                    y: {
                      scale: 'y',
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0] === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.scale(prev[8]),
                            d.scale(d.datum.y.value),
                          )(t);
                        }
                        return d.scale(d.datum.y.value);
                      },
                    },
                    size: {
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0] === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.resources.scale('r')(prev[9]),
                            d.resources.scale('r')(d.datum.size.value),
                          )(t);
                        }
                        return interpolate(0, d.resources.scale('r')(d.datum.size.value))(t);
                      },
                    },
                    sizeLimits: {
                      maxRelExtent: (2 / 9),
                    },
                    fill: (d) => colorScale(d.datum.color.value),
                    strokeWidth: 0,
                    opacity: (d) => {
                      if (selectedCountries.length) {
                        if (selectedCountries.includes(d.datum.country.value)) {
                          return 1;
                        }
                        return 0.1;
                      }
                      return 1;
                    },
                  },
                },
                {
                  key: 'tooltip',
                  type: 'tooltip',
                  displayOrder: 10,
                  settings: {
                    // Target point marker
                    filter: (nodes) => nodes.filter((node) => node.type === 'circle'),
                    // Extract data
                    extract: ({ node }) => {
                      const obj = {};
                      obj.medals = node.data.medals.label;
                      obj.country = node.data.country.label;
                      obj.yearCity = node.data.yearCity.label;
                      obj.medalsG = node.data.medalsG.label;
                      obj.medalsS = node.data.medalsS.label;
                      obj.medalsB = node.data.medalsB.label;
                      return obj;
                    },
                    content: ({ h, data }) => { // eslint-disable-line no-shadow
                      const els = [];
                      data.forEach((node) => {
                        els.push(h('div', { style: { fontSize: '12px', marginBottom: '10px' } }, `${node.country} - ${node.yearCity}`));
                        els.push(h('div', { style: { fontSize: '14px', marginBottom: '5px' } }, `${node.medals} ${node.medals === 1 ? 'medal' : 'medals'}`));
                        els.push(h('div', { style: { fontSize: '14px' } }, `${node.medalsG} 🥇  ${node.medalsS} 🥈  ${node.medalsB} 🥉`));
                      });
                      return h('div', {}, els);
                    },
                    placement: {
                      type: 'pointer',
                      area: 'target',
                      dock: 'auto',
                    },
                  },
                },
                {
                  type: 'labels',
                  displayOrder: 2,
                  settings: {
                    sources: [{
                      component: 'p',
                      selector: 'circle',
                      strategy: {
                        type: 'rows',
                        settings: {
                          direction: 'horizontal',
                          fontSize: 3.5,
                          justify: 0.5,
                          padding: 0.1,
                          fill: '#FFFFFF',
                          labels: [
                            {
                              placements: [{
                                position: 'inside',
                              }],
                              label: (node) => node.data.country.label,
                            },
                            {
                              placements: [{
                                position: 'outside',
                              }],
                              label: (node) => node.data.medals.label,
                            },
                          ],
                        },
                      },
                    }],
                  },
                },
              ],

              interactions: [
                {
                  type: 'native',
                  events: {
                    mousemove(e) {
                      this.chart.component('tooltip').emit('show', e);
                    },
                    mouseleave() {
                      this.chart.component('tooltip').emit('hide');
                    },
                  },
                },
                {
                  type: 'hammer',
                  gestures: [
                    {
                      type: 'Tap',
                      events: {
                        tap(e) {
                          this.chart.component('tooltip').emit('show', e);
                        },
                      },
                    },
                  ],
                },
              ],
            },
          });
          if (t === 1) {
            stopTransition();
            setPrevData(data);
          }
        });
      }, [data, instance]);

      useEffect(() => {
        if (!instance) {
          return;
        }
        instance.update();
      }, [rect.width, rect.height, instance]);
    },
  };
}
